// -----------------------------------------------------------------------------
// HubSidebarDetails
//
// The 'details' tab content for the CompletedTreatmentHub sidebar. Read-only
// compact treatment context (TreatmentContextHeader) + two light edit affordances:
//   1. "Change start date" — collapsed accordion → MUI X DateTimePicker. On
//      accept it dispatches updateTransitionTime with the same payload shape as
//      StartedDateField.jsx ({ timestamp: 'DD-MM-YYYY HH:mm:ss.0Z', lead, id,
//      state }). Carries a prominent reporting/analytics warning.
//   2. "Edit patient details" — deep-links to the lead detail page (one source
//      of truth; the edit form is NOT embedded here).
//
// treatmentId / treatmentStatus are resolved in the hub (resolveRowTreatment)
// and passed down so the transition update targets the right lead_treatment.
// -----------------------------------------------------------------------------
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
    Box, Typography, Button, Collapse,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { dispatch } from 'src/redux/store';
import { updateTransitionTime } from 'src/redux/slices/lead';
import { updateError } from 'src/redux/slices/user';
import { getTransitionDate } from 'src/utils/dataGridUtils';
import TreatmentContextHeader from './TreatmentContextHeader';

export default function HubSidebarDetails({ row, leadId, practiceId, treatmentId, treatmentStatus, handleClose, onUpdated }) {
    const navigate = useNavigate();
    const [showDateEditor, setShowDateEditor] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    // Seed the picker with the treatment's existing start date (the
    // `to_treatment_started` transition timestamp) so the current value shows
    // first — same source the table's Started-date column uses (getTransitionDate).
    // App uses AdapterDateFns, so the picker value must be a JS Date.
    const [dateValue, setDateValue] = useState(() => {
        // Match the hub's treatment by id. Only fall back to [0] when the hub has no
        // treatment id AND the list is a single treatment — never [0] of a multi-treatment lead.
        const txList = Array.isArray(row?.treatmentData) ? row.treatmentData : [];
        const startedLogs =
            txList.find((t) => String(t?.id) === String(treatmentId))?.state_transition_logs ||
            (treatmentId == null && txList.length === 1 ? txList[0]?.state_transition_logs : null) ||
            [];
        const raw = getTransitionDate(startedLogs);
        if (!raw) return null;
        const m = moment(raw, 'DD-MM-YYYY HH:mm:ss');
        return m.isValid() ? m.toDate() : null;
    });

    const canEditDate = leadId != null && treatmentId != null && treatmentStatus != null;

    const handleDateAccept = (newValue) => {
        if (!newValue) return;
        const detail = {
            // Same shape as StartedDateField.jsx → updateTransitionTime.
            timestamp: `${moment(newValue).format('DD-MM-YYYY HH:mm:ss')}.0Z`,
            lead: leadId,
            id: treatmentId,
            state: treatmentStatus,
        };
        dispatch(updateTransitionTime(detail, practiceId, (res) => {
            dispatch(updateError({
                success: res?.success,
                message: res?.message || (res?.success ? 'Start date updated.' : 'Failed to update start date.'),
            }));
            // Reload the grid behind the sidebar. Without this the save lands
            // and the snackbar confirms it, but the Started-date column keeps
            // showing the old value until the user reloads the page — which
            // reads exactly like the save failed. StartedDateField.jsx has
            // always refreshed on success; this surface was missing it.
            if (res?.success && onUpdated) onUpdated();
        }));
    };

    const goToEditPatient = () => {
        navigate(`/dashboard/practice/${practiceId}/lead-detail/${leadId}`);
        if (handleClose) handleClose();
    };

    return (
        <Box sx={{ px: 2, pt: 1.25, pb: 1, borderBottom: '1px solid rgba(145, 158, 171, 0.08)', flexShrink: 0 }}>
            <TreatmentContextHeader row={row} />

            {/* Change start date — compact collapsible row */}
            <Box
                component="button"
                type="button"
                data-testid="hub-change-start-date-toggle"
                onClick={() => setShowDateEditor((v) => !v)}
                disabled={!canEditDate}
                sx={{
                    mt: 1, width: '100%', display: 'flex', alignItems: 'center', gap: 1, px: 0.5, py: 0.75,
                    bgcolor: 'transparent', border: 'none', borderRadius: '8px', fontFamily: 'inherit', textAlign: 'left',
                    cursor: canEditDate ? 'pointer' : 'not-allowed',
                    '&:hover': canEditDate ? { bgcolor: 'rgba(0,0,0,0.03)' } : {},
                }}
            >
                <Iconify icon="solar:calendar-mark-bold-duotone" width={16} height={16} sx={{ color: '#078DEE', flexShrink: 0 }} />
                <Typography sx={{ flex: 1, fontSize: '12.5px', fontWeight: 600, color: '#1d1d1f' }}>Change start date</Typography>
                <Iconify icon={showDateEditor ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} width={16} height={16} sx={{ color: '#86868b' }} />
            </Box>
            <Collapse in={showDateEditor} unmountOnExit>
                <Box sx={{ pb: 1, pt: 0.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, p: 1, mb: 1, bgcolor: 'rgba(255, 149, 0, 0.10)', borderLeft: '3px solid #ff9500', borderRadius: '6px' }}>
                        <Iconify icon="bxs:info-circle" width={16} height={16} sx={{ color: '#ff9500', mt: 0.125, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '12px', color: '#1d1d1f', lineHeight: 1.4, fontWeight: 600 }}>
                            Changing this affects reporting &amp; analytics for this treatment.
                        </Typography>
                    </Box>
                    <DateTimePicker
                        label="Treatment start date"
                        open={pickerOpen}
                        onOpen={() => setPickerOpen(true)}
                        onClose={() => setPickerOpen(false)}
                        value={dateValue}
                        maxDateTime={new Date()}
                        ampm={false}
                        // `format`, not `inputFormat` (@mui/x-date-pickers v6; v5 name is silently ignored).
                        format="dd/MM/yyyy HH:mm"
                        onChange={(v) => setDateValue(v)}
                        onAccept={handleDateAccept}
                        slotProps={{
                            textField: { fullWidth: true, size: 'small', onKeyDown: (e) => e.preventDefault() },
                            actionBar: { actions: ['cancel', 'accept'] },
                        }}
                    />
                </Box>
            </Collapse>

            {/* Edit patient details — deep link */}
            <Button
                data-testid="hub-edit-patient-btn"
                size="small"
                startIcon={<Iconify icon="solar:user-id-bold-duotone" width={16} height={16} />}
                onClick={goToEditPatient}
                disabled={leadId == null}
                sx={{ textTransform: 'none', fontSize: '12.5px', fontWeight: 600, color: '#1d1d1f', justifyContent: 'flex-start', px: 0.5, minWidth: 0 }}
            >
                Edit patient details
            </Button>
        </Box>
    );
}

HubSidebarDetails.propTypes = {
    row: PropTypes.object,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    treatmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    treatmentStatus: PropTypes.string,
    handleClose: PropTypes.func,
    onUpdated: PropTypes.func,
};

HubSidebarDetails.defaultProps = {
    row: null,
    leadId: null,
    practiceId: null,
    treatmentId: null,
    treatmentStatus: null,
    handleClose: null,
    onUpdated: null,
};
